import { Button, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarClock,
  CalendarDays,
  Check,
  FileQuestion,
  type LucideIcon,
  Users,
  Wallet,
  UserRoundCheck,
} from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useGetSingleJobQuery } from "../../app/features/recruitment/recruitment.api";
import LoginModal from "../../components/LoginModal";
import useAuth from "../../hooks/useAuth";
import useDiscloser from "../../hooks/useDiscloser";
import WebLayout from "../../layouts/WebLayout";
import cn from "../../lib/cn";
import { formatDate } from "../../utils/date";
import {
  EmptyState,
  MetaItem,
  OrgAvatar,
  panelClass,
} from "./components/portal";
import {
  formatExperience,
  formatSalary,
  getDeadline,
} from "./components/portal.utils";
import { PATHS } from "../../routes/paths";

type ModalType = "LOGIN";

const OverviewRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) => (
  <div className="flex items-start gap-3 py-3">
    <Icon
      className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
      strokeWidth={1.75}
    />
    <div className="min-w-0">
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-gray-900">{children}</dd>
    </div>
  </div>
);

const RichSection = ({ title, html }: { title: string; html?: string }) =>
  html ? (
    <section className="py-8 first:pt-0">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <div
        className="ql-editor mt-3 !p-0 text-[15px] leading-7 text-gray-600"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </section>
  ) : null;

const JobDetails = () => {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  // "applied-jobs" is the value used by old links.
  const fromApplied = ["applications", "applied-jobs"].includes(
    params.get("from") ?? "",
  );
  const navigate = useNavigate();
  const { verifiedInfo } = useAuth();
  const [config, handleOpen, handleClose] = useDiscloser<ModalType>();

  const { data, isLoading, isError } = useGetSingleJobQuery(
    { userId: verifiedInfo?.id as string, id: jobId as string },
    { skip: !jobId },
  );

  const job = data?.data;
  const backPath = fromApplied
    ? PATHS.jobPortal.applications
    : PATHS.jobPortal.root;

  if (isLoading) {
    return (
      <WebLayout>
        <div className="flex h-[70vh] items-center justify-center">
          <Spin size="large" />
        </div>
      </WebLayout>
    );
  }

  if (isError || !job) {
    return (
      <WebLayout>
        <div className="mx-auto max-w-xl px-4 py-24">
          <EmptyState
            icon={FileQuestion}
            title="Job not found"
            description="This position may have been closed or removed."
            action={
              <Button
                onClick={() => navigate(PATHS.jobPortal.root)}
                className="btn-primary h-9 rounded-full px-5 font-medium"
              >
                Browse open jobs
              </Button>
            }
          />
        </div>
      </WebLayout>
    );
  }

  const hasApplied = job.has_applied || job.applied_status;
  const notOpenYet = dayjs(job.publish_date).isAfter(dayjs(), "day");
  const deadline = getDeadline(job.job_application_deadline);
  const canApply = !hasApplied && !notOpenYet;
  const salary = formatSalary(
    job.salary_min_bdt,
    job.salary_max_bdt,
    job.is_show_salary_public,
  );

  const handleApplyClick = () => {
    if (!verifiedInfo) {
      handleOpen({ type: "LOGIN" });
      return;
    }
    navigate(PATHS.jobPortal.apply(job.id));
  };

  const applyButton = (block?: boolean) =>
    hasApplied ? (
      <Button
        disabled
        block={block}
        size="large"
        className="h-11 rounded-full px-6 font-medium"
      >
        <Check className="h-4 w-4" />
        Applied
      </Button>
    ) : (
      <Tooltip
        title={
          notOpenYet
            ? `Applications open on ${formatDate(job.publish_date)}`
            : ""
        }
      >
        <Button
          block={block}
          size="large"
          disabled={!canApply}
          onClick={handleApplyClick}
          className={cn(
            "h-11 rounded-full px-6 font-medium",
            canApply && "btn-primary",
          )}
        >
          Apply now
        </Button>
      </Tooltip>
    );

  return (
    <WebLayout>
      <LoginModal
        data={config.data}
        type={config.type}
        handleClose={handleClose}
        open={config.open && config.type === "LOGIN"}
      />

      {/* ---------- Header ---------- */}
      <header className="relative overflow-hidden border-b border-gray-200 bg-white">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[420px] w-[620px] rounded-full bg-theme-primary/[0.12] blur-[120px]" />
        <div className="pointer-events-none absolute -right-40 top-10 h-[300px] w-[420px] rounded-full bg-theme-primary/[0.06] blur-[120px]" />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8"
        >
          <button
            onClick={() => navigate(backPath)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            {fromApplied ? "Back to applied jobs" : "Back to all jobs"}
          </button>

          <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="flex gap-5">
              <OrgAvatar name={job.org_name} size="lg" />
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  {job.job_title}
                </h1>
                <p className="mt-1 text-[15px] text-gray-500">
                  {job.org_name}
                  {job.department_name && ` · ${job.department_name}`}
                </p>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5">
                  <MetaItem icon={BriefcaseBusiness}>
                    {formatExperience(job.job_experience)}
                  </MetaItem>
                  <MetaItem icon={Wallet}>{salary}</MetaItem>
                  <MetaItem icon={Users}>
                    {job.vacancy_count} opening
                    {job.vacancy_count === 1 ? "" : "s"}
                  </MetaItem>
                </div>
              </div>
            </div>
            <div className="shrink-0">{applyButton()}</div>
          </div>
        </motion.div>
      </header>

      {/* ---------- Body ---------- */}
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_300px] lg:gap-14 lg:px-8 lg:py-14">
        <article className="min-w-0 divide-y divide-gray-200">
          <RichSection title="Job context" html={job.job_context} />
          <RichSection title="Job description" html={job.job_description} />
          <RichSection
            title="Educational requirements"
            html={job.educational_experience}
          />
          <RichSection
            title="Experience requirements"
            html={job.experience_requirement}
          />
          <RichSection
            title="Additional requirements"
            html={job.additional_requirement}
          />
          <RichSection
            title="Compensation & other benefits"
            html={job.compensation_and_benefit}
          />

          {job.skills?.length > 0 && (
            <section className="py-8">
              <h2 className="text-base font-semibold text-gray-900">Skills</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {job.skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[13px] text-gray-700"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="py-8">
            <h2 className="text-base font-semibold text-gray-900">
              About {job.org_name}
            </h2>
            <p className="mt-3 text-[15px] leading-7 text-gray-600">
              {job.org_name} is a forward-thinking organization dedicated to
              excellence and innovation. We pride ourselves on creating a
              collaborative and inclusive environment where every team member
              can thrive and grow professionally. Join us and be a part of our
              journey to success.
            </p>
          </section>
        </article>

        {/* Overview */}
        <aside>
          <div className={cn(panelClass, "p-5 lg:sticky lg:top-24")}>
            <h2 className="text-sm font-semibold text-gray-900">
              Job overview
            </h2>
            <dl className="mt-2 divide-y divide-gray-100">
              {job.employee_type && (
                <OverviewRow icon={UserRoundCheck} label="Employment type">
                  <span className="capitalize">
                    {job.employee_type.replace(/[_-]/g, " ").toLowerCase()}
                  </span>
                </OverviewRow>
              )}
              <OverviewRow icon={BriefcaseBusiness} label="Experience">
                {formatExperience(job.job_experience)}
              </OverviewRow>
              <OverviewRow icon={Wallet} label="Salary">
                {salary}
              </OverviewRow>
              <OverviewRow icon={Users} label="Openings">
                {job.vacancy_count}
              </OverviewRow>
              <OverviewRow icon={CalendarDays} label="Posted on">
                {formatDate(job.publish_date)}
              </OverviewRow>
              {job.job_application_deadline && (
                <OverviewRow icon={CalendarClock} label="Apply before">
                  {formatDate(job.job_application_deadline)}
                  {deadline && deadline.urgent && (
                    <span className="ml-2 text-xs font-medium text-amber-600">
                      {deadline.label}
                    </span>
                  )}
                </OverviewRow>
              )}
            </dl>
            <div className="mt-4">{applyButton(true)}</div>
          </div>
        </aside>
      </div>
    </WebLayout>
  );
};

export default JobDetails;
